/* eslint-disable max-classes-per-file */
import {
    AddTicketStateIdDocument,
    AddTicketStateIdMutation,
    AddTicketStateIdMutationVariables,
    ChatStatus,
    Dashboard, DashboardGroupBy, DashboardSortBy,
    TicketState,
} from "../../__generated__/api";
import client from "../../api";
import { LoggingHandler } from "../logging/LoggingContext";
import { ActionContext, ActionType } from "./action-registry";
import ColumnStateCache from "./column-state-cache";
import Filters from "./filters";
import SearchFilter from "./filters/search";
import GroupBy from "./group-by";
import { SearchContext } from "./search-state";
import SelectionState from "./selection";
import SortBy from "./sort-by";
import TicketColumn from "./ticket-column";
import TicketColumnGroupCache from "./ticket-column-group-cache";
import TicketColumnState from "./ticket-column-state";
import TicketItem from "./ticket-item";
import TicketItemCache from "./ticket-state";

function saveTicketStateIds(chatIds: string[], stateId: string) {
    LoggingHandler.instance.wrap(async () => {
        await client.mutate<AddTicketStateIdMutation, AddTicketStateIdMutationVariables>({
            mutation: AddTicketStateIdDocument,
            variables: {
                chatIds,
                stateId,
            },
        });
    });
}

export default class DashboardViewProcessor {
    public static create(
        tickets: TicketItemCache,
        currentDashboard: Dashboard,
        groupCache: TicketColumnGroupCache,
        columnCache: ColumnStateCache,
        selectionState: SelectionState,
        searchState: SearchContext,
        columnState: TicketColumnState,
    ) {
        const ticketGroups: Record<string, TicketColumn> = Object.create(null);

        const filterSettings = currentDashboard?.settings?.filters ?? {};
        const filters = Filters.getAll().filterMap(
            Filter => new Filter(filterSettings),
            filter => filter.shouldCreate(filterSettings),
        );
        if (searchState.isSearching && searchState.search) {
            filters.push(new SearchFilter(searchState.search));
        }

        const groupBy = currentDashboard?.settings?.groupBy ?? DashboardGroupBy.NoGroup;
        const GroupByCreator = GroupBy.getAll().find(
            grouper => grouper.shouldCreate(groupBy),
        );
        const groupByCreator = GroupByCreator ? new GroupByCreator(groupBy) : undefined;

        const sortBy = currentDashboard?.settings?.sortBy ?? DashboardSortBy.LastResponseDateDescending;
        const Sorter = SortBy.getAll().find(
            sorter => sorter.shouldCreate(sortBy),
        );
        if (!Sorter) {
            throw new Error("No sorter found.");
        }

        columnState.setDashboardId(currentDashboard?.id);

        const sorter = new Sorter(sortBy);
        tickets.getTickets().forEach(ticketItem => {
            if (filters.some(filter => !filter.shouldInclude(ticketItem))) {
                return;
            }
            const ticketState = columnCache.getForTicket(ticketItem);
            if (!ticketState) {
                return;
            }

            ticketGroups[ticketState.id] ??= new TicketColumn(
                ticketState,
                groupCache,
                groupByCreator,
                sorter,
                columnState,
            );
            ticketGroups[ticketState.id].addTicket(ticketItem);
        });

        const groups = currentDashboard?.ticketStates.mapFilter(({ ticketStateId }) => {
            const group = ticketGroups[ticketStateId];
            if (group) {
                return group;
            }

            const state = columnCache.getById(ticketStateId);
            if (state) {
                return new TicketColumn(
                    state as TicketState,
                    groupCache,
                    groupByCreator,
                    sorter,
                    columnState,
                );
            }
            return undefined;
        });
        return new DashboardViewProcessor(groups, selectionState, columnCache);
    }

    // eslint-disable-next-line no-useless-constructor
    private constructor(
        private groups: TicketColumn[],
        private selectionState: SelectionState,
        private columnCache: ColumnStateCache,
    ) {
        // none
    }

    public addTicket(ticket: TicketItem) {
        const ticketState = this.columnCache.getForTicket(ticket);
        if (!ticketState) {
            return undefined;
        }

        const column = this.getColumn(ticketState.id);
        if (!column) {
            return undefined;
        }

        column.addTicket(ticket);
        return column;
    }

    public getData(): TicketColumn[] {
        return this.groups ?? [];
    }

    public getColumn(id: string): TicketColumn | undefined {
        return this.groups.find(col => col.ticketState.id === id);
    }

    public updateAll() {
        this.groups?.forEach(group => group.groups.forEach(ticketGroup => {
            ticketGroup.update();
        }));
    }

    public tryCollapseAll() {
        this.groups?.forEach(group => group.groups.forEach(ticketGroup => {
            ticketGroup.collapse();
        }));
    }

    public tryExpandAll() {
        this.groups?.forEach(group => group.groups.forEach(ticketGroup => {
            ticketGroup.expand();
        }));
    }

    public tryToggleAll() {
        if (this.hasExpandedGroup) {
            this.tryCollapseAll();
        } else {
            this.tryExpandAll();
        }
    }

    public get hasExpandedGroup() {
        return this.groups?.some(group => group.groups.some(ticketGroup => ticketGroup.isExpanded));
    }

    public moveTickets(tickets: TicketItem[], target: TicketColumn, actionContext: ActionContext, onDone?: (() => void)) {
        const original: {ticket: TicketItem, stateId: string, status: ChatStatus, assigneeId: string }[] = [];
        const newStatus = target.ticketState.chatStatuses.firstElement;

        const targetStatuses = new Set(target.ticketState.chatStatuses);
        const statusChangedTickets = tickets.filter(ticket => !targetStatuses.has(ticket.status));

        const needUpdate: string[] = [];
        tickets.forEach(ticket => {
            const ticketState = this.columnCache.getForTicket(ticket);
            if (!ticketState || ticketState.id === target.ticketState.id) {
                return;
            }
            needUpdate.push(ticket.id);
            original.push({
                ticket, stateId: ticketState.id, status: ticket.status, assigneeId: ticket.chat.assigneeId,
            });
            const currentColumn = this.getColumn(ticketState.id);
            currentColumn?.removeTicket(ticket);
            ticket.setTicketStateIdLocal(target.ticketState.id, newStatus);
            target.addTicket(ticket);
        });

        this.selectionState.update();

        const handleDone = () => {
            saveTicketStateIds(needUpdate, target?.ticketState.id);
            onDone?.();
        };

        if (target.ticketState.chatStatuses?.isEmpty || statusChangedTickets.isEmpty) {
            handleDone?.();
            return;
        }

        const onRollback = () => {
            original.forEach(({
                ticket, stateId, status, assigneeId,
            }) => {
                const oldTarget = this.getColumn(stateId);
                if (!oldTarget) {
                    return;
                }
                target?.removeTicket(ticket);
                oldTarget.addTicket(ticket);
                ticket.setTicketStateIdLocal(stateId, status, assigneeId);
            });

            this.selectionState.update();
        };

        if (newStatus === ChatStatus.Assigned) {
            this.selectionState.execute(ActionType.ASSIGN_SELF, statusChangedTickets, actionContext, handleDone, onRollback);
        } else if (newStatus === ChatStatus.ChatRequested) {
            this.selectionState.execute(ActionType.UNASSIGN, statusChangedTickets, actionContext, handleDone, onRollback);
        } else if (newStatus === ChatStatus.Ignored) {
            this.selectionState.execute(ActionType.IGNORE, statusChangedTickets, actionContext, handleDone, onRollback);
        } else if (newStatus === ChatStatus.OnHold) {
            this.selectionState.execute(ActionType.HOLD, statusChangedTickets, actionContext, handleDone, onRollback);
        } else if (newStatus === ChatStatus.Snoozed) {
            this.selectionState.execute(ActionType.SNOOZE, statusChangedTickets, actionContext, handleDone, onRollback);
        } else if (newStatus === ChatStatus.Finished) {
            this.selectionState.execute(ActionType.CLOSE, statusChangedTickets, actionContext, handleDone, onRollback);
        }
    }
}
